import React from "react";
import TrainingStoreDisplay from "./TrainingStoreDisplay";
import LocationTrackMatrix from "./LocationTrackMatrix";
import { getRightworkAPI } from "../requests/utilities/requests";
import { postRightworkAPI } from "../requests/utilities/requests";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LoadSpinner from "../../../utilities/LoadSpinner";
import {
  API_get_stores_with_learning_track_enrollment,
  API_get_learning_track_enrollments_by_employee_for_store,
  API_post_learning_track_enrollment,
  API_post_bulk_learning_track_enrollment,
  API_post_learning_track_unenrollment,
} from "../../../constants";
import SchoolIcon from "@mui/icons-material/School";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AssessmentIcon from "@mui/icons-material/Assessment";

function TrainingOverviewDisplay(props) {
  const [showStoreTraining, setShowStoreTraining] = React.useState(false);

  const [allStoresTrainingData, setAllStoresTrainingData] = React.useState([]);
  // Full set of non-draft tracks for the company — the column universe for the
  // Location x Track matrix, so published tracks show even with nobody enrolled.
  const [publishedTracks, setPublishedTracks] = React.useState([]);
  const [
    storeDetailTrainingDataByEmployee,
    setStoreDetailTrainingDataByEmployee,
  ] = React.useState([]);

  const [selectedStore, setSelectedStore] = React.useState(null);

  const [isLoading, setIsLoading] = React.useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = React.useState(false);

  const homeCompany = props.storeNames?.filter(
    (s) => s.id === props.homeStore
  )[0]["company"];

  // [GET] api/lms_info/get_stores_with_learning_track_enrollment
  // * company_id
  const fetchStoresWithLearningTrackEnrollment = async (showLoading) => {
    setIsLoading(showLoading);
    try {
      const res = await getRightworkAPI(
        API_get_stores_with_learning_track_enrollment,
        { company_id: homeCompany }
      );
      if (res.status === 200) {
        setAllStoresTrainingData(res.data.redux?.payload?.stores);
        setPublishedTracks(res.data.redux?.payload?.published_tracks || []);
        setIsLoading(false);
      }
    } catch (error) {
      throw Error(error);
    }
  };

  // [GET] api/lms_info/get_learning_track_enrollments_by_employee_for_store
  // * store_id
  const fetchStoreDetailTrainingDataByEmployee = async (
    showLoading,
    store_id
  ) => {
    setIsLoading(showLoading);
    try {
      const res = await getRightworkAPI(
        API_get_learning_track_enrollments_by_employee_for_store,
        { store_id: store_id }
      );
      if (res.status === 200) {
        setStoreDetailTrainingDataByEmployee(
          res.data.redux?.payload?.store_team_members
        );
        setIsLoading(false);
      }
    } catch (error) {
      throw Error(error);
    }
  };

  // ----------- POST APIs ----------- //

  // [POST] /lms_info/post_learning_track_enrollment
  // * learning_track_uuid, company_id, store_team_member_id, completion_due_date (optional)
  const post_learning_track_enrollment = async (
    learning_track_uuid,
    store_team_member_id,
    completion_due_date = null
  ) => {
    const requestData = {
      company_id: homeCompany,
      learning_track_uuid: learning_track_uuid,
      store_team_member_id: store_team_member_id,
    };

    if (completion_due_date !== null) {
      requestData.completion_due_date = completion_due_date;
    }

    try {
      toast.loading("Assigning Track...", { autoClose: false });

      const res = await postRightworkAPI(
        API_post_learning_track_enrollment,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Track Assigned Successfully", { autoClose: 3000 });

        fetchStoreDetailTrainingDataByEmployee(false, selectedStore.id);
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error);
    }
  };

  // [POST] /lms_info/post_bulk_learning_track_enrollment
  // * learning_track_uuid, company_id, store_team_member_ids[], completion_due_date (optional)
  const post_bulk_learning_track_enrollment = async (
    learning_track_uuid,
    store_team_member_ids,
    completion_due_date = null
  ) => {
    const requestData = {
      company_id: homeCompany,
      learning_track_uuid: learning_track_uuid,
      store_team_member_ids: store_team_member_ids,
    };

    if (completion_due_date !== null) {
      requestData.completion_due_date = completion_due_date;
    }

    try {
      toast.loading("Assigning Track...", { autoClose: false });

      const res = await postRightworkAPI(
        API_post_bulk_learning_track_enrollment,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        const enrolled =
          res.data?.redux?.payload?.enrolled ?? store_team_member_ids.length;
        const skipped = res.data?.redux?.payload?.skipped_already_enrolled ?? 0;
        const plural = enrolled === 1 ? "" : "s";
        const message =
          skipped > 0
            ? `Assigned to ${enrolled} team member${plural} (${skipped} already enrolled)`
            : `Assigned to ${enrolled} team member${plural}`;
        toast.success(message, { autoClose: 3000 });

        fetchStoreDetailTrainingDataByEmployee(false, selectedStore.id);
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error);
    }
  };

  // [POST] api/lms_info/post_learning_track_unenrollment
  // * company_id, learning_track_uuid, store_team_member_id
  const post_learning_track_unenrollment = async (
    learning_track_uuid,
    store_team_member_id
  ) => {
    const requestData = {
      company_id: homeCompany,
      learning_track_uuid: learning_track_uuid,
      store_team_member_id: store_team_member_id,
    };

    try {
      toast.loading("Unassigning Track...", { autoClose: false });

      const res = await postRightworkAPI(
        API_post_learning_track_unenrollment,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Track Unassigned Successfully", { autoClose: 3000 });

        fetchStoreDetailTrainingDataByEmployee(false, selectedStore.id);
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error);
    }
  };

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await fetchStoresWithLearningTrackEnrollment(true);
      } finally {
        if (!cancelled) setInitialLoadComplete(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Published (non-draft) tracks for the company — the column count of the
  // Location x Track matrix, surfaced as a header stat. Unions the published
  // universe with any enrolled track uuids as a safety net.
  const totalTrackCount = React.useMemo(() => {
    const uuids = new Set();
    publishedTracks.forEach((t) => {
      if (t?.track_uuid) uuids.add(t.track_uuid);
    });
    allStoresTrainingData.forEach((store) => {
      (store?.learning_track_enrollment_summary_by_track || []).forEach((t) => {
        if (t?.track_uuid) uuids.add(t.track_uuid);
      });
    });
    return uuids.size;
  }, [allStoresTrainingData, publishedTracks]);

  // Drill from a location row in the matrix into its per-employee Track Status
  // Matrix.
  const onSelectStore = (store) => {
    fetchStoreDetailTrainingDataByEmployee(true, store.id);
    setSelectedStore(store);
    setShowStoreTraining(true);
  };

  return (
    <>
      {initialLoadComplete && !isLoading ? (
        <div className="min-h-screen bg-white">
          {!showStoreTraining ? (
            <>
              {/* Header Section */}
              <div className="bg-white border-b border-gray-200">
                <div className="px-6 py-8">
                  <div className="mb-4">
                    <h1 className="text-2xl font-bold text-slate-900 mb-1">
                      Training Overview
                    </h1>
                    <p className="text-sm text-slate-600">
                      Monitor training progress across your organization
                    </p>
                  </div>

                  {/* Stats Row */}
                  <div className="flex items-center gap-6 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <LocationOnIcon style={{ fontSize: "18px" }} />
                      <span>{allStoresTrainingData.length} Locations</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <AssessmentIcon style={{ fontSize: "18px" }} />
                      <span>
                        {Math.round(
                          allStoresTrainingData.reduce((acc, store) => {
                            const progress =
                              store.learning_track_enrollment_summary
                                .total_sections_denomenator > 0
                                ? (store.learning_track_enrollment_summary
                                    .total_sections_numerator /
                                    store.learning_track_enrollment_summary
                                      .total_sections_denomenator) *
                                  100
                                : 0;
                            return acc + progress;
                          }, 0) / Math.max(allStoresTrainingData.length, 1)
                        )}
                        % Average Completion
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <SchoolIcon style={{ fontSize: "18px" }} />
                      <span>
                        {totalTrackCount}{" "}
                        {totalTrackCount === 1 ? "Track" : "Tracks"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <LocationTrackMatrix
                stores={allStoresTrainingData}
                publishedTracks={publishedTracks}
                onSelectStore={onSelectStore}
              />
            </>
          ) : (
            <TrainingStoreDisplay
              storeDetailTrainingDataByEmployee={
                storeDetailTrainingDataByEmployee
              }
              selectedStore={selectedStore}
              homeCompany={homeCompany}
              onBackToOverview={() => setShowStoreTraining(false)}
              post_learning_track_enrollment={post_learning_track_enrollment}
              post_bulk_learning_track_enrollment={
                post_bulk_learning_track_enrollment
              }
              post_learning_track_unenrollment={post_learning_track_unenrollment}
              fetchStoreDetailTrainingDataByEmployee={
                fetchStoreDetailTrainingDataByEmployee
              }
              proficiencyStarsCount={props.proficiencyStarsCount}
              proficiencyFromLMSEnabled={props.proficiencyFromLMSEnabled}
            />
          )}
        </div>
      ) : (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <LoadSpinner />
        </div>
      )}
    </>
  );
}

export default TrainingOverviewDisplay;
